import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Search,
  ArrowLeft,
  MessageSquare,
  Paperclip,
  Smile,
  Video,
  Phone,
  MoreVertical,
  Camera,
  IndianRupee,
  Mic,
  Sparkles,
  Trash2,
  Send,
  Loader2,
} from "lucide-react";
import { leadsApi, whatsappApi } from "../services/api";
import WhatsAppChatView from "./WhatsAppChatView";
import WhatsAppComposer from "./Whatsappcomposer";
import { normalizeWhatsAppPhone } from "../utils/helpers";
import { VoiceNoteRecorder } from "../utils/audioEncoder";


export default function FloatingWhatsAppDrawer({ isOpen, setIsOpen }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sendCount, setSendCount] = useState(0);
  const [timeStr, setTimeStr] = useState("12:30");
  const [textMessage, setTextMessage] = useState("");
  const [sendingText, setSendingText] = useState(false);
  const [showAttachmentSheet, setShowAttachmentSheet] = useState(false);
  const [attachmentType, setAttachmentType] = useState("text");
  const inputRef = useRef(null);

  // Audio Voice Note Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioFileInputRef = useRef(null);



  // Dynamic status bar time
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTimeStr(
        now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseDrawer = () => {
    setIsOpen(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setSearching(true);
    try {
      const data = await leadsApi.searchLeads(query.trim());
      const list = Array.isArray(data)
        ? data
        : data?.content || data?.data || [];
      setResults(list);
    } catch (err) {
      console.error("Lead search failed in drawer:", err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setShowAttachmentSheet(false);
    setTextMessage("");
  };

  const handleBackToSearch = () => {
    cancelRecordingAudio();
    setSelectedLead(null);
    setShowAttachmentSheet(false);
    setTextMessage("");
  };

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const voiceRecorderRef = useRef(null);

  const startRecordingAudio = async () => {
    try {
      const recorder = new VoiceNoteRecorder();
      await recorder.start();
      voiceRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingDuration(0);

      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (err) {
      console.error("Microphone access error:", err);
      alert("Microphone access denied or error: " + err.message);
    }
  };

  const cancelRecordingAudio = () => {
    if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.cancel();
      voiceRecorderRef.current = null;
    }
    setIsRecording(false);
    setRecordingDuration(0);
  };

  const stopAndSendAudio = async () => {
    if (!voiceRecorderRef.current || !selectedPhone) return;
    if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);

    setIsRecording(false);
    setUploadingAudio(true);

    try {
      const audioBlob = await voiceRecorderRef.current.stop();
      voiceRecorderRef.current = null;

      // Create pure MP3 audio file
      const audioFile = new File([audioBlob], `voice_${Date.now()}.mp3`, {
        type: "audio/mpeg",
      });

      const normalized = normalizeWhatsAppPhone(selectedPhone);
      
      // 1. Upload audio file to backend (S3 as .mp3)
      const uploadRes = await whatsappApi.uploadMedia(audioFile, "audio");
      const audioUrl = uploadRes?.url || uploadRes?.data?.url || uploadRes?.mediaUrl;

      if (!audioUrl) {
        throw new Error(uploadRes?.error || uploadRes?.message || "Failed to upload audio recording.");
      }

      // 2. Send WhatsApp Audio message
      const sendRes = await whatsappApi.sendAudio(normalized, audioUrl);
      if (sendRes && (sendRes.success || sendRes.status === "success" || sendRes.messageId)) {
        setSendCount((c) => c + 1);
      } else {
        alert("Failed to send audio note: " + (sendRes?.error || sendRes?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Audio send failed:", err);
      alert("Audio send failed: " + err.message);
    } finally {
      setUploadingAudio(false);
      setRecordingDuration(0);
    }
  };


  const handleAudioFileSelect = async (e) => {

    const file = e.target.files?.[0];
    if (!file || !selectedPhone) return;

    setUploadingAudio(true);
    try {
      const normalized = normalizeWhatsAppPhone(selectedPhone);
      const uploadRes = await whatsappApi.uploadMedia(file, "audio");
      const audioUrl = uploadRes?.url || uploadRes?.data?.url || uploadRes?.mediaUrl;

      if (!audioUrl) {
        throw new Error(uploadRes?.error || uploadRes?.message || "Failed to upload audio.");
      }

      const sendRes = await whatsappApi.sendAudio(normalized, audioUrl);
      if (sendRes && (sendRes.success || sendRes.status === "success" || sendRes.messageId)) {
        setSendCount((c) => c + 1);
      } else {
        alert("Failed to send audio: " + (sendRes?.error || sendRes?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Failed to send audio file:", err);
      alert("Failed to send audio file: " + err.message);
    } finally {
      setUploadingAudio(false);
      if (audioFileInputRef.current) audioFileInputRef.current.value = "";
    }
  };




  const handleSendTemplate = async (templateName = "hello_world") => {
    if (!selectedPhone) return;
    setSendingText(true);
    try {
      const normalized = normalizeWhatsAppPhone(selectedPhone);
      const response = await whatsappApi.sendTemplate(normalized, templateName);
      if (response && (response.success || response.status === "success" || response.messageId)) {
        alert(`✅ Template "${templateName}" sent successfully! Conversation initiated.`);
        setSendCount((c) => c + 1);
        setShowAttachmentSheet(false);
      } else {
        alert("Failed to send template: " + (response?.error || response?.message || "Unknown error"));
      }
    } catch (err) {
      alert("Failed to send template: " + err.message);
    } finally {
      setSendingText(false);
    }
  };

  const handleSendText = async (e) => {
    if (e) e.preventDefault();
    if (!textMessage.trim() || !selectedPhone) return;

    setSendingText(true);
    try {
      const normalized = normalizeWhatsAppPhone(selectedPhone);
      const msg = textMessage.trim();
      let response;

      // If message is hello_world or template prefix, send directly as Meta template
      if (msg === "hello_world" || msg.startsWith("template:")) {
        const tmplName = msg.replace("template:", "").trim() || "hello_world";
        response = await whatsappApi.sendTemplate(normalized, tmplName, "en_US");
      } else {
        response = await whatsappApi.sendText(normalized, msg);
      }

      if (response && (response.success || response.status === "success" || response.messageId)) {
        setTextMessage("");
        setSendCount((c) => c + 1);
      } else {
        const errorMsg = String(response?.error || response?.message || "");
        if (
          errorMsg.includes("24 hours") ||
          errorMsg.includes("131047") ||
          errorMsg.includes("outside") ||
          errorMsg.includes("template") ||
          errorMsg.includes("window")
        ) {
          const tryTemplate = window.confirm(
            "Customer has not messaged within 24 hours (Meta WhatsApp Policy).\n\nWould you like to send an Approved Template (hello_world) to initiate the chat?"
          );
          if (tryTemplate) {
            await handleSendTemplate("hello_world");
            setTextMessage("");
          }
        } else {
          alert(
            "Failed to send message: " + (response?.error || response?.message || "Unknown error"),
          );
        }
      }
    } catch (err) {
      console.error("Failed to send text message:", err);
      alert("Failed to send message: " + err.message);
    } finally {
      setSendingText(false);
    }
  };


  const selectedPhone =
    selectedLead?.contactNumber || selectedLead?.phoneNumber || "";


  return (
    <div
      className="fixed right-4 top-1/2 z-[9999] w-[390px] h-[820px] bg-[#1a1a1a] rounded-[48px] p-1.5 shadow-[-12px_12px_40px_rgba(0,0,0,0.3)] border-[4px] border-[#333] transition-transform duration-300 ease-in-out flex flex-col select-none"
      style={{
        fontFamily: "Inter, system-ui, sans-serif",
        transform: isOpen
          ? "translate3d(0, -50%, 0)"
          : "translate3d(calc(100% + 16px), -50%, 0)",
        marginTop: "36px",
      }}
    >
      {/* ── Curved Floating Tab (sticks out on the left edge of the phone bezel) ── */}
      <button
        onClick={handleToggle}
        className="absolute left-0 bottom-[130px] -translate-x-full bg-white py-4 px-3 rounded-l-2xl shadow-[-6px_4px_16px_rgba(0,0,0,0.12)] border-l border-y border-gray-100 flex items-center justify-center group focus:outline-none transition-all duration-300 hover:pr-4 z-40"
        title="Open WhatsApp Chat"
      >
        <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shadow-md hover:scale-105 active:scale-95 transition-transform duration-200">
          {/* Custom SVG for WhatsApp bubble logo */}
          <svg
            className="w-7 h-7 text-white fill-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>
      </button>

      {/* ── Speaker Grill ── */}
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-1 bg-[#444] rounded-full z-30" />


      {/* ── Green Trapezoidal Base Stand ── */}
      <div
        className="absolute bottom-[-17px] left-1/2 -translate-x-1/2 z-[-1] pointer-events-none"
        style={{
          width: "410px",
          height: "18px",
          background: "linear-gradient(to bottom, #075E54, #054c44)",
          clipPath: "polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)",
          filter: "drop-shadow(0px 8px 10px rgba(0, 0, 0, 0.4))",
        }}
      />

      {/* ── Side Physical Buttons (Volume Rocker & Power Button) ── */}
      <div
        className="absolute right-[-7px] top-[150px] w-[3px] h-[75px] bg-[#444] border-y border-r border-[#666] rounded-r-md z-30 shadow-[1px_1px_2px_rgba(0,0,0,0.3)]"
        title="Volume Rocker"
      />
      <div
        className="absolute right-[-7px] top-[240px] w-[3px] h-[45px] bg-[#444] border-y border-r border-[#666] rounded-r-md z-30 shadow-[1px_1px_2px_rgba(0,0,0,0.3)]"
        title="Power Button"
      />

      {/* ── Inner Screen Container ── */}
      <div className="relative w-full h-full bg-[#075E54] rounded-[42px] overflow-hidden flex flex-col border-2 border-black">
        {/* ── Camera Notch ── */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-black border border-[#222] z-30" />

        {/* ── Status Bar ── */}
        <div className="bg-[#075E54] text-white text-[11px] px-6 pt-3.5 pb-1 flex justify-between items-center z-20 font-semibold select-none flex-shrink-0">
          <div>{timeStr}</div>
          <div className="flex items-center gap-1.5">
            {/* Signal Strength */}
            <svg className="w-3.5 h-3 fill-current" viewBox="0 0 24 24">
              <path d="M2 22h20V2z" />
            </svg>
            {/* Wi-Fi Icon */}
            <svg className="w-3.5 h-3 fill-current" viewBox="0 0 24 24">
              <path d="M12 21a2 2 0 110-4 2 2 0 010 4zm7.071-7.071a10 10 0 00-14.142 0 1 1 0 11-1.414-1.414 12 12 0 0116.97 0 1 1 0 01-1.414 1.414zm-2.828-2.828a6 6 0 00-8.486 0 1 1 0 11-1.414-1.414 8 8 0 0111.314 0 1 1 0 01-1.414 1.414z" />
            </svg>
            {/* Battery Icon */}
            <svg
              className="w-4 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <rect x="2" y="6" width="16" height="12" rx="2" />
              <path d="M22 10v4" />
              <rect
                x="5"
                y="9"
                width="8"
                height="6"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </div>
        </div>

        {/* ── Screen Main Content ── */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 bg-[#075E54] -mt-[1px]">
          {!selectedLead ? (
            /* ── View A: WhatsApp Conversations (Search Screen) ── */
            <div className="flex flex-col flex-1 min-h-0 bg-white">
              {/* WhatsApp Chats Header */}
              <div className="bg-[#075E54] text-white px-4 py-[17px] flex items-center justify-between z-20 shadow-md flex-shrink-0">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-white fill-current"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="font-bold text-lg">WhatsApp</span>
                </div>
              </div>

              {/* Chat Search Area */}
              <div className="p-3 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search name or number..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-[#075E54] bg-white transition-shadow"
                    />
                    <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  </div>
                  <button
                    type="submit"
                    disabled={searching}
                    className="bg-[#075E54] hover:bg-[#0b665c] text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center justify-center disabled:opacity-50 min-w-[55px]"
                  >
                    {searching ? "..." : "Search"}
                  </button>
                </form>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {results.length === 0 && !searching && (
                  <div className="p-8 text-center flex flex-col items-center justify-center text-gray-400 gap-2">
                    <MessageSquare className="w-10 h-10 text-gray-300" />
                    <span className="text-xs">
                      Search for a lead to start chatting.
                    </span>
                  </div>
                )}
                {results.map((lead) => {
                  const leadDisplayName = (lead.customerName || lead.name || lead.fullName || lead.contactNumber || lead.phoneNumber || "Unnamed").trim();
                  const initial = (lead.customerName || lead.name || lead.fullName || "U").trim()[0]?.toUpperCase() || "U";
                  return (
                    <div
                      key={lead.id}
                      onClick={() => handleSelectLead(lead)}
                      className="px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3 group"
                    >
                      <div className="w-11 h-11 rounded-full bg-[#128c7e]/10 text-[#128c7e] flex items-center justify-center font-bold text-base flex-shrink-0">
                        {initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline mb-0.5">
                          <h4 className="font-bold text-sm text-gray-800 truncate group-hover:text-[#128c7e] transition-colors">
                            {leadDisplayName}
                          </h4>
                          <span className="text-[10px] text-gray-400">Now</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {lead.contactNumber || lead.phoneNumber || "-"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* ── View B: Live Chat Screen (Phone Styling) ── */
            <div className="flex flex-col flex-1 min-h-0 bg-[#efeae2] relative">
              {/* WhatsApp Active Chat Header */}
              <div className="bg-[#075E54] text-white px-3 py-[15px] flex items-center justify-between z-20 shadow-md flex-shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  {/* Back to search arrow */}
                  <button
                    onClick={handleBackToSearch}
                    className="p-1 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors text-white focus:outline-none"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  {/* Small Avatar */}
                  <div className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {((selectedLead.customerName || selectedLead.name || selectedLead.fullName || "U").trim()[0] || "U").toUpperCase()}
                  </div>
                  {/* Lead Info */}
                  <div className="min-w-0">
                    <h4 className="font-bold text-xs text-white truncate leading-tight">
                      {selectedLead.customerName || selectedLead.name || selectedLead.fullName || selectedLead.contactNumber || selectedLead.phoneNumber || "Customer"}
                    </h4>
                    <span className="text-[9px] text-white/80 block leading-tight">
                      {selectedLead.contactNumber || selectedLead.phoneNumber || "online"}
                    </span>
                  </div>
                </div>
                {/* Right Call & Options Icons */}
                <div className="flex items-center gap-2">
                  <button className="p-1 text-white hover:bg-white/10 rounded-full transition-colors">
                    {/* Video Call Icon */}
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button className="p-1 text-white hover:bg-white/10 rounded-full transition-colors">
                    {/* Phone Call Icon */}
                    <svg
                      className="w-3.5 h-3.5 text-white fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M21.384 17.791l-3.769-1.615a1 1 0 00-1.127.23l-1.662 1.662a15.5 15.5 0 01-6.76-6.76l1.662-1.662a1 1 0 00.23-1.127L8.344 2.766a1 1 0 00-1.118-.621H3.618a1 1 0 00-.99 1.114 18 18 0 0017.65 17.65 1 1 0 001.114-.99v-3.608a1 1 0 00-.621-1.118z" />
                    </svg>
                  </button>
                  <button className="p-1 text-white hover:bg-white/10 rounded-full transition-colors">
                    {/* Three Dots */}
                    <svg
                      className="w-4 h-4 text-white fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 10a2 2 0 110 4 2 2 0 010-4zm0-6a2 2 0 110 4 2 2 0 010-4zm0 12a2 2 0 110 4 2 2 0 010-4z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Scrollable Chat Area */}
              <div className="flex-1 overflow-hidden flex flex-col min-h-0 bg-[#efeae2] relative">
                <div className="whatsapp-chat-container-reset flex-1 flex flex-col min-h-0 w-full p-2">
                  <WhatsAppChatView
                    phoneNumber={selectedPhone}
                    refreshKey={sendCount}
                  />
                </div>
              </div>

              {/* WhatsApp Message Input Bar */}
              <div className="p-2 bg-[#efeae2] flex items-center gap-1.5 z-30 flex-shrink-0">
                {isRecording ? (
                  /* ── Live Audio Recording Mode ── */
                  <div className="flex-1 flex items-center justify-between bg-white border border-red-200 rounded-full px-3 py-1.5 shadow-sm min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping flex-shrink-0" />
                      <span className="text-xs font-bold text-red-600 font-mono flex-shrink-0">
                        {Math.floor(recordingDuration / 60)
                          .toString()
                          .padStart(2, "0")}
                        :
                        {(recordingDuration % 60)
                          .toString()
                          .padStart(2, "0")}
                      </span>
                      <span className="text-[10px] text-gray-500 italic truncate">
                        Recording audio...
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={cancelRecordingAudio}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors"
                        title="Cancel recording"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={stopAndSendAudio}
                        disabled={uploadingAudio}
                        className="px-3 py-1 bg-[#128c7e] hover:bg-[#0b665c] text-white text-xs font-semibold rounded-full flex items-center gap-1 transition-transform active:scale-95 shadow-sm disabled:opacity-75"
                        title="Send Voice Note"
                      >
                        {uploadingAudio ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5" />
                            <span>Send</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal Message Input ── */
                  <form
                    onSubmit={handleSendText}
                    className="flex-1 flex items-center gap-1.5 min-w-0"
                  >
                    <div className="flex-1 bg-white border border-gray-200 rounded-full px-3 py-2 text-[11px] flex items-center gap-2 shadow-sm min-w-0">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 focus:outline-none flex-shrink-0"
                      >
                        <Smile className="w-4.5 h-4.5" />
                      </button>
                      <input
                        ref={inputRef}
                        type="text"
                        placeholder="Message"
                        value={textMessage}
                        onChange={(e) => setTextMessage(e.target.value)}
                        className="w-full focus:outline-none text-[11px] text-gray-800 bg-transparent min-w-0"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAttachmentType("document");
                          setShowAttachmentSheet(true);
                        }}
                        className="text-gray-400 hover:text-[#075E54] focus:outline-none flex-shrink-0"
                        title="Attach file/options"
                      >
                        <Paperclip className="w-4.5 h-4.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAttachmentType("camera");
                          setShowAttachmentSheet(true);
                        }}
                        className="text-gray-400 hover:text-[#075E54] focus:outline-none flex-shrink-0"
                      >
                        <Camera className="w-4.5 h-4.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAttachmentSheet(false);
                          setTextMessage("hello_world");
                          setTimeout(() => inputRef.current?.focus(), 50);
                        }}
                        className="text-[#8B5CF6] hover:text-[#7C3AED] hover:scale-110 active:scale-95 transition-transform focus:outline-none flex-shrink-0"
                        title="Quick Fill Template (hello_world)"
                      >
                        <Sparkles className="w-4.5 h-4.5" />
                      </button>
                    </div>

                    <button
                      type={textMessage.trim() === "" ? "button" : "submit"}
                      onClick={textMessage.trim() === "" ? (e) => { e.preventDefault(); startRecordingAudio(); } : undefined}
                      disabled={sendingText || uploadingAudio}
                      className="w-9 h-9 rounded-full bg-[#128c7e] hover:bg-[#0b665c] text-white flex items-center justify-center flex-shrink-0 transition-transform active:scale-90 shadow-md disabled:opacity-80 cursor-pointer"
                      title={textMessage.trim() === "" ? "Click to Record Voice Note" : "Send Message"}
                    >
                      {uploadingAudio ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      ) : textMessage.trim() === "" ? (
                        <Mic className="w-4 h-4 text-white" />
                      ) : (
                        <svg
                          className="w-3.5 h-3.5 text-white fill-current translate-x-0.5"
                          viewBox="0 0 24 24"
                        >
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                      )}
                    </button>
                  </form>
                )}
              </div>



              {/* ── Slide-up Rich Attachment Sheet ── */}
              {showAttachmentSheet && (
                <div className="absolute inset-0 bg-black/40 z-40 flex flex-col justify-end">
                  {/* Click outside to close */}
                  <div
                    className="flex-1"
                    onClick={() => {
                      setShowAttachmentSheet(false);
                      setAttachmentType("text");
                    }}
                  />
                  {/* Slide-up Content */}
                  <div className="bg-white rounded-t-2xl max-h-[85%] overflow-y-auto p-4 relative shadow-2xl flex flex-col min-h-[320px]">
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100 mb-3 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-700">
                        Attach Media & Templates
                      </span>
                      <button
                        onClick={() => {
                          setShowAttachmentSheet(false);
                          setAttachmentType("text");
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <X className="w-4.5 h-4.5" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      <WhatsAppComposer
                        key={selectedLead.id}
                        defaultTo={selectedPhone}
                        initialMessageType={attachmentType}
                        onSent={(data) => {
                          console.log(
                            "WhatsApp sent from drawer composer:",
                            data,
                          );
                          setSendCount((c) => c + 1);
                          setShowAttachmentSheet(false);
                          setAttachmentType("text");
                          setTextMessage("");
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Styled Override for WhatsAppChatView when inside Drawer */}
      <style>{`
        /* Reset WhatsAppChatView width to fit perfectly inside the drawer content width */
        .whatsapp-chat-container-reset > div {
          width: 100% !important;
          max-width: 100% !important;
          border: none !important;
          box-shadow: none !important;
          border-radius: 0px !important;
          height: 100% !important;
          flex: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          background: transparent !important;
        }
        .whatsapp-chat-container-reset > div > div:first-child {
          display: none !important; /* Hide redundant inner header */
        }
        .whatsapp-chat-container-reset > div > div:nth-child(2) {
          background: transparent !important; /* Make scroll area transparent to see body beige color */
          padding: 8px 4px !important;
        }
      `}</style>
    </div>
  );
}
